import { Component, useEffect, useRef, type ErrorInfo, type ReactNode } from 'react'
import { RouteFallback } from './route-fallback'
import { retryLazyRoutes } from '../stores/lazy-route-store'
import {
  decideLazyRouteRecovery,
  hasAttemptedChunkReload,
  isBrowserOnline,
  isChunkLoadError,
  markChunkReloadAttempted,
} from '../utils/chunk-load-error'

type ErrorStateProps = {
  /** A failed chunk fetch, as opposed to a page that threw while rendering. */
  chunkError: boolean
  offline: boolean
  onRetry: () => void
  onReload: () => void
}

/**
 * Full-surface error state for the route area. The boundary renders instead of
 * the routes, so the nav is gone at this point — this has to stand alone and
 * offer its own way out.
 */
export function LazyRouteErrorState({ chunkError, offline, onRetry, onReload }: ErrorStateProps) {
  const retryRef = useRef<HTMLButtonElement>(null)

  // The routes vanished from under the user; move focus to the primary recovery
  // action so keyboard and screen-reader users are not left on a detached node.
  useEffect(() => {
    retryRef.current?.focus()
  }, [])

  const heading = offline
    ? 'You’re offline'
    : chunkError
      ? 'Couldn’t finish loading this page'
      : 'Something went wrong'

  const detail = offline
    ? 'This part of the app still needs to download, and your device has no connection. Reconnect, then try again.'
    : chunkError
      ? 'Part of the app couldn’t be downloaded. This usually means a new version was just released, or the connection dropped mid-download.'
      : 'This page ran into an unexpected error. Trying again will reload it from scratch.'

  return (
    <div
      role="alert"
      aria-labelledby="lazy-route-error-heading"
      className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10"
    >
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
        <h1 id="lazy-route-error-heading" className="text-base font-semibold text-slate-900">
          {heading}
        </h1>
        <p className="mt-2 text-sm text-slate-600">{detail}</p>
        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            ref={retryRef}
            onClick={onRetry}
            className="a11y-touch-target w-full rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={onReload}
            className="a11y-touch-target w-full rounded-xl border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Reload the app
          </button>
        </div>
      </div>
    </div>
  )
}

type Props = {
  children: ReactNode
  /** Seam for tests; production always performs a real browser reload. */
  reload?: () => void
}

type State = {
  error: unknown
  /** An automatic hard reload has been chosen — keep the splash up until it lands. */
  reloading: boolean
}

/**
 * Error boundary around the lazily-loaded routes.
 *
 * Without this, a rejected `import()` propagates past the route `Suspense` and
 * leaves the user on the loading splash forever — no error, no way out. The
 * boundary turns that into either a silent self-heal (stale deploy) or an
 * accessible error state with working recovery actions. See
 * `utils/chunk-load-error` for how the two failure modes are told apart.
 */
export class LazyRouteBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: false }

  static getDerivedStateFromError(error: unknown): State {
    // Decide here (a pure read) so a stale-deploy reload never flashes the error
    // card on its way out; the reload itself is claimed in componentDidCatch.
    const recovery = decideLazyRouteRecovery({
      error,
      online: isBrowserOnline(),
      reloadAlreadyAttempted: hasAttemptedChunkReload(),
    })
    return { error, reloading: recovery === 'reload' }
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('Route chunk failed to load', error, info.componentStack)
    if (!this.state.reloading) return
    if (markChunkReloadAttempted()) {
      this.doReload()
      return
    }
    // The marker could not be persisted, so a reload might loop — show the error
    // state and let the user decide instead.
    this.setState({ reloading: false })
  }

  private doReload = (): void => {
    const { reload } = this.props
    if (reload) {
      reload()
      return
    }
    window.location.reload()
  }

  /** User-initiated reload: no loop guard needed, it cannot run unattended. */
  private handleReload = (): void => {
    this.setState({ reloading: true })
    this.doReload()
  }

  /**
   * Re-attempt the import. Bumping the generation replaces the memoised (and
   * permanently rejected) lazy components before the subtree remounts.
   */
  private handleRetry = (): void => {
    retryLazyRoutes()
    this.setState({ error: null, reloading: false })
  }

  render(): ReactNode {
    const { error, reloading } = this.state
    if (reloading) return <RouteFallback />
    if (error != null) {
      return (
        <LazyRouteErrorState
          chunkError={isChunkLoadError(error)}
          offline={!isBrowserOnline()}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      )
    }
    return this.props.children
  }
}
